import { useEffect, useState } from 'react'

import { COLS } from 'src/components/Responsive'

/** Same breakpoint the `<Mobile>` / `<Desktop>` render-props use. */
export const MOBILE_MAX_WIDTH: number = (COLS as Record<string, number>).mobile

const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`

const readMatch = (): boolean => {
  //  SSR and the jsdom test env can both lack matchMedia; assume desktop rather
  //  than throwing, since the mobile path is the reduced one.
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false
  }
  return window.matchMedia(QUERY).matches
}

/**
 * Viewport-driven mobile flag.
 *
 * Subscribes with `addEventListener` where available and falls back to the
 * deprecated `addListener` — Safari only gained the modern API in 14, and the
 * fallback is what keeps the switcher from freezing on the value it had at
 * mount for those users.
 */
export const useIsMobile = (): boolean => {
  'use memo'

  const [isMobile, setIsMobile] = useState<boolean>(readMatch)

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined
    }

    const list = window.matchMedia(QUERY)
    const onChange = () => setIsMobile(list.matches)

    onChange()

    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    }

    // Deprecated, but it is the only listener API Safari < 14 exposes — without
    // it the flag freezes at its mount value on those browsers.
    list.addListener(onChange)
    return () => list.removeListener(onChange)
  }, [])

  return isMobile
}

export default useIsMobile
